import { HttpResponse, ServiceClient } from '@johnlewispartnership/wtr-website-api-client';
import config from 'config';
import { logApiError, logApiInfo } from 'logger';
import { safeUrlConcat } from 'utils/url';
import getApiBaseUrl from 'utils/getApiBaseUrl';
import getCorrelationIdFromResponse from 'utils/get-correlation-id-from-response';
import {
  GetOrderError,
  GetOrderResponse,
  OrderOptions,
  PlaceOrder409Error,
  PlaceOrderError,
  PlaceOrderResponse,
  PlaceOrderResult,
} from './types';

const orderOrchestrationApiUrl = getApiBaseUrl(config.services.orderOrchestration.path);

const serviceName = 'orderOrchestration';

class OrderOrchestrationServiceClient extends ServiceClient {
  async getOrder({
    authHeader,
    customerOrderId,
  }: {
    authHeader: string;
    customerOrderId: string;
  }): Promise<{
    order?: GetOrderResponse;
    error?: any;
  }> {
    const path = `/v1/orders/${customerOrderId}`;
    const fullApiUrl = safeUrlConcat(orderOrchestrationApiUrl, path);
    const response: HttpResponse<GetOrderResponse> = await this.httpClient
      .get<GetOrderResponse>(path, {
        headers: {
          Authorization: authHeader,
        },
        timeout: 45000,
      })
      .catch(error => {
        const errorResponse = {
          error,
        } as HttpResponse<GetOrderResponse>;

        return errorResponse;
      });

    if (!response.data || response.error) {
      const getOrderError: GetOrderError = {
        message: `Error getting order: ${response.error?.message || 'Unknown error'}`,
      };
      logApiError({
        correlationId: getCorrelationIdFromResponse(response),
        payload: {
          service: serviceName,
          url: path,
          message: getOrderError.message,
          duration: response.duration,
          httpStatus: `${response.status}`,
          httpStatusCode: response.status,
        },
      });

      return {
        order: response.data,
        error: getOrderError,
      };
    }

    logApiInfo({
      correlationId: getCorrelationIdFromResponse(response),
      payload: {
        duration: response.duration,
        service: serviceName,
        url: fullApiUrl,
        message: `Order details loaded from server, request took ${response.duration}ms`,
        httpStatus: String(response.status),
        httpStatusCode: response.status,
      },
    });

    return {
      order: response.data,
    };
  }

  async placeOrder({
    authHeader,
    customerOrderId,
    orderOptions,
  }: {
    authHeader: string;
    customerOrderId: string;
    orderOptions: OrderOptions;
  }): Promise<PlaceOrderResult> {
    const path = `/v1/orders/${customerOrderId}/place`;
    const fullApiUrl = safeUrlConcat(orderOrchestrationApiUrl, path);
    const response: HttpResponse<PlaceOrderResponse> = await this.httpClient
      .patch<OrderOptions & { event: string }, PlaceOrderResponse>(
        path,
        {
          event: 'PLACE',
          ...orderOptions,
        },
        {
          headers: {
            Authorization: authHeader,
          },
          timeout: 45000,
        },
      )
      .catch(error => {
        const errorResponse = {
          error,
        } as HttpResponse<PlaceOrderError>;

        return errorResponse;
      });

    if (!response.data || response.error) {
      logApiError({
        correlationId: getCorrelationIdFromResponse(response),
        payload: {
          service: serviceName,
          url: path,
          message: `Error placing order: ${response.error?.message || 'Unknown error'}`,
          duration: response.duration,
          /**
           * This field has a different format according to our logging standards (e.g.: "200 (OK)" ).
           * It's a string value, which means we can't do queries like "httpStatus > 200 AND httpStatus < 300".
           * We might want to get the standards changed.
           * See logging standards: https://www.jlpit.com/wiki/pages/viewpage.action?spaceKey=WD&title=Waitrose+E-commerce+logging+standards
           */
          httpStatus: `${response.status}`,
          // This field is not documented in the logging standards, but it's a number, so we can use relational operators in our queries.
          httpStatusCode: response.status,
        },
      });

      return {
        ...(response.error && {
          error:
            response.data && (response.data as PlaceOrder409Error).code
              ? (response.data as PlaceOrder409Error)
              : {
                  code: response.error.timeout ? 'TIMEOUT' : 'UNKNOWN_ERROR',
                },
        }),
      };
    }

    const placeOrderResponse = response.data as GetOrderResponse;

    logApiInfo({
      correlationId: getCorrelationIdFromResponse(response),
      payload: {
        duration: response.duration,
        service: serviceName,
        url: fullApiUrl,
        message: `Order placed, request took ${response.duration}ms`,
        httpStatus: String(response.status),
        httpStatusCode: response.status,
      },
    });
    return {
      order: placeOrderResponse,
    };
  }
}

const orderOrchestrationServiceClient = new OrderOrchestrationServiceClient(
  orderOrchestrationApiUrl,
);

export default orderOrchestrationServiceClient;
